/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
import { formatMoney } from '@shopify/theme-currency'
import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'
import classes from '~/config/classes'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  if (!product) {
    return false
  }

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    metafields: formatMetafields(product.metafields),
    options: formatOptions(product),
    price: formatPrice(product.priceRange),
    media: formatMedia(product),
    tags: product.tags || [],
    title: product.title,
    type: product.productType,
    url: `//${location.host}/products/${product.handle}`,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  if (product.collections) {
    formattedProduct.collections = formatProductCollections(product.collections)
  }

  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats product from Boost API Call
 * @param {Object} product
 * @returns {Object}
 */
 export function formatBoostProduct(product) {
  const formattedProduct = {
    available: product.available,
    collections: product.collections,
    compareAtPrice: formatPrice({
      maxVariantPrice: {
        amount: product.compare_at_price_max,
      },
      minVariantPrice: {
        amount: product.compare_at_price_min,
      },
    }),
    description: product.body_html,
    handle: product.handle,
    id: product.id,
    images: formatBoostImages(product),
    isBoost: true,
    metafields: formatMetafields(product.metafields),
    options: formatBoostOptions(product),
    price: formatPrice({
      maxVariantPrice: {
        amount: product.price_max,
      },
      minVariantPrice: {
        amount: product.price_min,
      },
    }),
    swatchOptions: formatBoostOptions(product) ? formatBoostOptions(product) : null,
    tags: product.tags || [],
    title: product.title,
    type: product.product_type,
    url: `//${location.host}/products/${product.handle}`,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatBoostVariants(product, formattedProduct.giftCard)
  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Combines product response with English swatch options.
 * @param {Object} product - Formatted product.
 * @param {Object} response - Options GraphQL response (in English).
 * @returns {Object}
 */
export function formatSwatchOptions(product, response) {
  const englishOptions = response.product || response
  const isGiftCard = (convertToHandle(product.type) === 'gift-card')

  const swatchOptions = product.options.map((option, optionIndex) => {
    const englishOption = englishOptions.options[optionIndex]

    return {
      ...option,
      name: englishOption.name,
      values: isGiftCard ? option.values : englishOption.values,
    }
  })

  return {
    ...product,
    swatchOptions,
  }
}

/**
 * Formats boost swatch elements and style.
 * @param {Object} value - Swatch color.
 * @param {Object} type - Type of return. Either image, color or border.
 * @returns {Object}
 */
export function formatBoostSwatch(value, type) {
  if (type === 'image') {
    const handle = value.toLowerCase().split(' ').join('-').split('/').join('')
    const baseSwatch = `swatch-${handle}.png`
    const swatchUrl = `${window.cnvs.urls.files.replace('placeholder', baseSwatch)}`
    return `url(${swatchUrl})`
  }

  if (type === 'color') {
    const fallBack = value.toLowerCase().split(' ').join('').split('/').join('')
    return fallBack
  }

  if (type === 'border') {
    if (!window.cnvs.boostSwatchBorders) {
      return
    }

    const handle = value.toLowerCase().split(' ').join('-').split('/').join('')

    if (window.cnvs.boostSwatchBorders.includes(handle)) {
      return classes.hasBorder
    }
  }
}

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @returns {Object}
 */
function formatPrice(price) {
  return {
    amount: (Number(price?.minVariantPrice.amount) * 100) || false,
    currencyCode: price?.minVariantPrice.currencyCode || window.cnvs.store.currency,
    max: (Number(price?.maxVariantPrice.amount) * 100) || false,
    min: (Number(price?.minVariantPrice.amount) * 100) || false,
    varies: (price?.maxVariantPrice.amount !== price?.minVariantPrice.amount),
  }
}

function formatProductCollections(collections) {
  return transformEdges(collections)
    .filter(collection => collection.onlineStoreUrl)
    .map(collection => ({
      handle: collection.handle,
      title: collection.title,
    }))
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
export function formatImages(product) {
  return transformEdges(product.images).map((image) => {
    const linkedVariant = transformEdges(product.variants).find((variant) => {
      return variant.image.id === image.id
    })
    return {
      ...formatImage(image),
      variant: decodeApiId(linkedVariant?.id) || false,
    }
  })
}

/**
 * Formats an image
 * @param {Object} image - Image
 * @returns Formatted image
 */
export function formatImage (image) {
    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.originalSrc,
      width: image.width,
    }
}


/**
 * Formats product's media.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  if (!product.media) return null
  return transformEdges(product.media).map(media => {
    let src
    const mediaContentType = media.mediaContentType.toLowerCase()
    switch (mediaContentType) {
      case 'external_video':
        src = media.embeddedUrl
        break
      case 'image':
        src = media.image.originalSrc
        break
      case 'video':
        src = media.sources?.[0]?.url
        break
      default:
        break
    }

    return {
      alt: media.alt,
      src,
      previewSrc: media.previewImage.originalSrc,
      mediaContentType,
    }
  })
}

/**
 * Formats boost images
 * @param {Object} product
 * @returns {Object}
 */
 function formatBoostImages(product) {
  return product.images_info.map((image) => {
    return {
      alt: image.alt,
      height: image.height,
      id: image.id,
      src: image.src,
      width: image.width,
    }
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats boost options
 * @param {Object} product
 * @returns {Object}
 */
 function formatBoostOptions(product) {
  return product.options_with_values.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values.map((option) => {
        return option.title
      }),
    }
  })
}

/**
 * Formats variants.
 * @param {Object} product - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compareAtPriceV2?.amount) * 100) || false,
        currencyCode: variant.compareAtPriceV2?.currencyCode || false,
      },
      id: decodeApiId(variant.id),
      sku: variant.sku,
      image: decodeApiId(variant.image?.id) || false,
      inventory: variant.quantityAvailable,
      metafields: formatMetafields(variant.metafields),
      price: {
        amount: (Number(variant.priceV2.amount) * 100),
        currencyCode: variant.priceV2.currencyCode,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Format boost variants
 * @param {Object} product
 * @param {Boolean} isGiftCard
 * @returns {Array}
 */
 function formatBoostVariants(product, isGiftCard) {
  return product.variants.map((variant) => {
    const image = product.images_info.find((image) => {
      return image.src === variant.image
    })
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compare_at_price) * 100) || false,
        currencyCode: window.cnvs.store.currency || false,
      },
      id: variant.id,
      image: image ? image.id : null,
      inventory: variant.inventory_quantity,
      price: {
        amount: (Number(variant.price) * 100),
        currencyCode: window.cnvs.store.currency || false,
      },
      selectedOptions: variant.merged_options.map((option) => {
        const data = option.split(':')
        return {
          name: data[0],
          value: variant.title,
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
     formattedVariant.title = isGiftCard
     ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
     : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response = {}) {
  const collection = response.collection ? response.collection : response.collectionByHandle

  if (!collection) {
    return
  }

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    title: collection.title,
    url: `//${location.host}/collections/${collection.handle}`,
    image: collection.image,
    metafields: formatMetafields(collection.metafields),
  }

  /**
   * Format product.
   */
   formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    const productImages = transformEdges(product.images).map((images) => {
      return images
    })

    return {
      handle: product.handle,
      images: productImages,
    }
  })

  return formattedCollection
}

/**
 * Format metafields
 * @param {Array} metafields - Array of metafields
 * @returns {{}}
 */
export function formatMetafields(metafields) {
  const transformedMetafields = {}
  if (!metafields) {
    return transformedMetafields
  }

  metafields.filter(metafield => metafield !== null).forEach(metafield => {
    if (!transformedMetafields[metafield.namespace]) {
      transformedMetafields[metafield.namespace] = {}
    }
    transformedMetafields[metafield.namespace][metafield.key] = {
      type: metafield.type,
      value: metafield.value,
    }
  })
  return transformedMetafields
}
